// Issue + PullRequest — tracked GitHub entities the agent acts on.

import { z } from 'zod';
import {
  IssueIdSchema,
  PullRequestIdSchema,
  RepoIdSchema,
  SessionIdSchema,
} from '../value-objects/ids';

export const IssueState = {
  Open: 'open',
  Closed: 'closed',
} as const;
export const IssueStateSchema = z.enum(IssueState);
export type IssueState = z.infer<typeof IssueStateSchema>;

export const Qualification = {
  Bug: 'bug',
  Feature: 'feature',
  Dupe: 'dupe',
  Question: 'question',
  Unknown: 'unknown',
} as const;
export const QualificationSchema = z.enum(Qualification);
export type Qualification = z.infer<typeof QualificationSchema>;

export const HandoffStatus = {
  Pending: 'pending',
  Accepted: 'accepted',
  Declined: 'declined',
} as const;
export const HandoffStatusSchema = z.enum(HandoffStatus);
export type HandoffStatus = z.infer<typeof HandoffStatusSchema>;

export const IssueSchema = z.object({
  id: IssueIdSchema,
  repoId: RepoIdSchema,
  githubNumber: z.number().int().positive(),
  githubId: z.number().int().positive(),
  /**
   * Cached GitHub title (#980). Defaulted, not required, mirroring the DB
   * column (`notNull default ''`): rows written before the cache carry '' and
   * pre-title fixtures keep parsing. Renderers fall back to `#<number>` on ''.
   */
  title: z.string().default(''),
  state: IssueStateSchema,
  qualification: QualificationSchema,
  handoffStatus: HandoffStatusSchema.nullable(),
  lastSessionId: SessionIdSchema.nullable(),
  lastAgentActionAt: z.date().nullable(),
});
export type Issue = z.infer<typeof IssueSchema>;

export const PullRequestState = {
  Open: 'open',
  Closed: 'closed',
  Merged: 'merged',
} as const;
export const PullRequestStateSchema = z.enum(PullRequestState);
export type PullRequestState = z.infer<typeof PullRequestStateSchema>;

export const CiStatus = {
  Pending: 'pending',
  Success: 'success',
  Failure: 'failure',
  Neutral: 'neutral',
  Unknown: 'unknown',
} as const;
export const CiStatusSchema = z.enum(CiStatus);
export type CiStatus = z.infer<typeof CiStatusSchema>;

export const MergeDecision = {
  Auto: 'auto',
  Ask: 'ask',
  Block: 'block',
} as const;
export const MergeDecisionSchema = z.enum(MergeDecision);
export type MergeDecision = z.infer<typeof MergeDecisionSchema>;

/**
 * WHY a PR's `merge_decision` was stamped `ask` (#5670) — the stored vocabulary of
 * `pull_requests.merge_ask_cause`, declared once here because db (the CHECK that
 * restates it), worker, agents-manager, api and the dashboard all read it and domain
 * is their one common dependency. `packages/db/src/merge-ask-cause-schema.test.ts`
 * pins the CHECK against this list in both directions, so a member added here is a
 * migration of that CHECK and a member removed is unreadable rows.
 *
 * The first three are the worker's re-ask causes — the `kind` literals of its
 * `MergeAskCause` union (`apps/worker/src/babysit/merge-reask.ts`), which this list
 * is deliberately not named after. The fourth, `session_escalation`, the worker never
 * stamps: it belongs to the agent-session ask-writer (the sibling issue under #5667).
 */
export const MergeAskCauseKind = {
  StaleApproval: 'stale_approval',
  AskBeforeActing: 'ask_before_acting',
  HumanMergeLane: 'human_merge_lane',
  SessionEscalation: 'session_escalation',
  /** A person took a recorded `auto`/`block` back (`undoPullRequestDecisionAudited`). */
  DecisionUndone: 'decision_undone',
} as const;
export const MergeAskCauseKindSchema = z.enum(MergeAskCauseKind);
export type MergeAskCauseKind = z.infer<typeof MergeAskCauseKindSchema>;

export const PullRequestSchema = z.object({
  id: PullRequestIdSchema,
  repoId: RepoIdSchema,
  githubNumber: z.number().int().positive(),
  githubId: z.number().int().positive(),
  /** Cached PR title (#852) — `''` when the webhook never carried one. */
  title: z.string(),
  /** PR author's GitHub login — null when the projection never saw one. */
  authorLogin: z.string().nullable(),
  state: PullRequestStateSchema,
  ciStatus: CiStatusSchema,
  reviewersSeen: z.array(z.string()),
  readySignals: z.array(z.string()),
  mergeDecision: MergeDecisionSchema.nullable(),
  lastSessionId: SessionIdSchema.nullable(),
});
export type PullRequest = z.infer<typeof PullRequestSchema>;
